import { rgba } from 'polished';
import Quill from 'quill';
import Delta from 'quill-delta';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { LocalizationScope, useLocalize, useLocalizedValue } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Portal, withFormik } from '@treams/treams-ui';

import { tokens } from '../../theme/tokens';
import { InlineToolbar } from './InlineToolbar';
import { QuillEditor } from './quill/QuillEditor';
import { QuillToolbar } from './quill/QuillToolbar';

const Container = styled(Box)<{ focus?: boolean }>`
	border-radius: 6px;
	background: ${tokens.surface.white};
	box-shadow: 0px 0px 0px 1px ${tokens.border.subtle};
	overflow: hidden;
	width: 100%;

	${(props) =>
		props.focus &&
		`
	outline: none;
	box-shadow: 0px 0px 0px 1px ${props.theme.colors.primary},
		0px 0px 0px 3px ${rgba(props.theme.colors.primary, 0.25)};
	`};
`;

const Editor = styled(Box)<{ focus?: boolean }>`
	background: ${tokens.surface.white};
	box-shadow: 0px 1px 2px ${rgba(tokens.border.subtle, 0.5)} inset;
`;

/**
 * A declarative keyboard shortcut for a Quill editor. The handler runs in the
 * capture phase on the editor root, so it fires before Quill's own keyboard
 * module (e.g. to repurpose Tab). Modifier fields default to `false` and must
 * match the event exactly.
 */
export type QuillKeyboardShortcut = {
	key: string;
	shiftKey?: boolean;
	metaKey?: boolean;
	ctrlKey?: boolean;
	handler: (event: KeyboardEvent) => void;
};

/**
 * Binds `keyboardShortcuts` to a Quill instance in the capture phase, with
 * cleanup. The latest shortcuts are read through a ref so changing the array
 * (identity or contents) never re-binds the listener.
 * @param quill - The Quill instance to bind to, if any.
 * @param shortcuts - The shortcuts to match against keydown events.
 */
function useQuillKeyboardShortcuts(
	quill: Quill | undefined,
	shortcuts: QuillKeyboardShortcut[] | undefined,
) {
	const shortcutsRef = useRef(shortcuts);
	shortcutsRef.current = shortcuts;

	useEffect(() => {
		if (!quill) {
			return;
		}

		const { root } = quill;

		const handleKeyDown = (event: KeyboardEvent) => {
			const shortcut = shortcutsRef.current?.find(
				(candidate) =>
					candidate.key === event.key &&
					(candidate.shiftKey ?? false) === event.shiftKey &&
					(candidate.metaKey ?? false) === event.metaKey &&
					(candidate.ctrlKey ?? false) === event.ctrlKey,
			);

			if (!shortcut) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			shortcut.handler(event);
		};

		root.addEventListener('keydown', handleKeyDown, true);

		return () => {
			root.removeEventListener('keydown', handleKeyDown, true);
		};
	}, [quill]);
}

type Props = {
	hasMediaUpload?: boolean;
	hasHeading?: boolean;
	hideLink?: boolean;
	toolbarIsInline?: boolean;
	hasRightAlignment?: boolean;
	autoFocus?: boolean;
	value: Delta | undefined;
	placeholder?: string;
	toolbarMessage?: string;
	minHeight?: number | string;
	onChange: (value: Delta) => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
	keyboardShortcuts?: QuillKeyboardShortcut[];
	onQuill?: (quill?: Quill) => void;
};

function UnmemoizedQuillInput({
	hasMediaUpload,
	hasHeading,
	hideLink,
	toolbarIsInline,
	hasRightAlignment,
	autoFocus,
	value,
	placeholder,
	toolbarMessage,
	minHeight,
	onChange,
	onKeyDown,
	keyboardShortcuts,
	onQuill,
}: Props) {
	const quillRef = useRef<Quill>();
	const [quillInstance, setQuillInstance] = useState<Quill | undefined>(undefined);
	const [focus, setFocus] = useState(autoFocus);
	const [toolbarPos, setToolbarPos] = useState<{ top: number; left: number } | null>(null);

	useQuillKeyboardShortcuts(quillInstance, keyboardShortcuts);

	const handleQuillInstance = useCallback(
		(quillEditor?: Quill) => {
			if (quillEditor) {
				setQuillInstance(quillEditor);
				onQuill?.(quillEditor);
			}
		},
		[onQuill],
	);

	useEffect(() => {
		if (autoFocus) {
			setFocus(true);
		}
	}, [autoFocus]);

	useEffect(() => {
		if (!toolbarIsInline || !quillInstance) {
			return;
		}

		const quill = quillInstance;

		const updateToolbarPos = () => {
			const range = quill.getSelection();
			if (range && range.length >= 1) {
				const bounds = quill.getBounds(range);
				const editorRect = quill.container.getBoundingClientRect();

				const windowWidth = window.innerWidth;
				const widthToolbar = 250;

				if (bounds) {
					setToolbarPos({
						top: editorRect.top + bounds.top + window.scrollY - 50,
						left:
							editorRect.left +
							bounds.left +
							window.scrollX -
							(editorRect.left + bounds.left + window.scrollX + widthToolbar / 2 + 55 >
							windowWidth - 32
								? 400
								: 200),
					});
				}
			} else {
				setToolbarPos(null);
			}
		};

		quill.on('selection-change', updateToolbarPos);
		updateToolbarPos();

		return () => {
			quill.off('selection-change', updateToolbarPos);
		};
	}, [toolbarIsInline, quillInstance]);

	const handleQuillRef = useCallback(
		(quillEditor?: Quill) => {
			if (quillEditor) {
				quillRef.current = quillEditor;
				setQuillInstance(quillEditor);
				onQuill?.(quillEditor);
			}
		},
		[onQuill],
	);

	if (toolbarIsInline) {
		return (
			<>
				{quillInstance && toolbarPos && (
					<Portal>
						<div
							style={{
								position: 'absolute',
								top: toolbarPos.top,
								left: toolbarPos.left,
								zIndex: 2000,
							}}
						>
							<InlineToolbar quill={quillInstance} onChange={onChange} />
						</div>
					</Portal>
				)}
				<QuillEditor
					autoFocus={focus}
					p={3}
					value={value}
					onChange={onChange}
					onKeyDown={onKeyDown}
					onQuill={handleQuillInstance}
					onFocus={() => setFocus(true)}
					onBlur={() => setFocus(false)}
					placeholder={placeholder}
					theme="bubble"
					minHeight={minHeight}
				/>
			</>
		);
	}

	return (
		<Container focus={focus}>
			<QuillToolbar
				hasMediaUpload={hasMediaUpload}
				hasHeading={hasHeading}
				hideLink={hideLink}
				hasRightAlignment={hasRightAlignment}
				innerRef={quillRef}
				onChange={onChange}
				message={toolbarMessage}
			/>
			<Editor focus={focus}>
				<QuillEditor
					autoFocus={focus}
					p={3}
					value={value}
					onChange={onChange}
					onKeyDown={onKeyDown}
					onQuill={handleQuillRef}
					onFocus={() => setFocus(true)}
					onBlur={() => setFocus(false)}
					placeholder={placeholder}
					minHeight={minHeight}
				/>
			</Editor>
		</Container>
	);
}

/**
 * This is necessary to prevent unnecessary re-renders of the QuillEditor component.
 */
const MemoizedQuillInput = memo<Props>(UnmemoizedQuillInput);

export function QuillInput(props: Props) {
	return <MemoizedQuillInput {...props} />;
}

QuillInput.Formik = withFormik(QuillInput);

function LocalizableQuillInputInner(props: {
	autoFocus?: boolean;
	hasMediaUpload?: boolean;
	maxHeight?: number | string;
	value: graphql.LocalizedAttributedString;
	placeholder?: string;
	keyboardShortcuts?: QuillKeyboardShortcut[];
	onChange: (value: graphql.LocalizedAttributedString) => void;
	onQuill?: (quill?: Quill) => void;
}) {
	const ref = useRef<Quill>();
	const [quillInstance, setQuillInstance] = useState<Quill | undefined>(undefined);
	const [focus, setFocus] = useState(props.autoFocus);
	const [value, setValue] = useLocalizedValue(props.value, props.onChange, new Delta());
	const { language } = useLocalize();

	useQuillKeyboardShortcuts(quillInstance, props.keyboardShortcuts);

	// Some locations might provide an empty array, the default value
	// should be an empty Delta for 1 current language.
	useEffect(() => {
		if (props.value.length === 0) {
			props.onChange([{ languageID: language.id, text: new Delta() }]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.value]);

	return (
		<Container focus={focus}>
			<QuillToolbar hasMediaUpload={props.hasMediaUpload} innerRef={ref} localizable />
			<Editor focus={focus}>
				<QuillEditor
					key={language.id}
					autoFocus={props.autoFocus}
					p={3}
					maxHeight={props.maxHeight}
					overflowY={props.maxHeight ? 'auto' : undefined}
					value={value}
					onChange={(newValue) => {
						setValue(newValue);
					}}
					onQuill={(quillEditor) => {
						ref.current = quillEditor;
						setQuillInstance(quillEditor);
						props.onQuill?.(quillEditor);
					}}
					onFocus={() => setFocus(true)}
					onBlur={() => setFocus(false)}
					placeholder={props.placeholder}
				/>
			</Editor>
		</Container>
	);
}

function LocalizableQuillInput(props: {
	autoFocus?: boolean;
	hasMediaUpload?: boolean;
	maxHeight?: number | string;
	value: graphql.LocalizedAttributedString;
	placeholder?: string;
	keyboardShortcuts?: QuillKeyboardShortcut[];
	onChange: (value: graphql.LocalizedAttributedString) => void;
	onQuill?: (quill?: Quill) => void;
}) {
	return (
		<LocalizationScope>
			<LocalizableQuillInputInner {...props} />
		</LocalizationScope>
	);
}

QuillInput.Localizable = LocalizableQuillInput;
LocalizableQuillInput.Formik = withFormik(LocalizableQuillInput);
